import { MigratableJson } from '../MigratableJson';
import { FORM_NODE_NAME } from '@builder4/shared/utils/constants';
import type { NodeDetachablePropsState } from '@builder4/shared/types/craft';

/**
 * Migration v8: Migrates Form component from old breakpoint system (detachablePropsState)
 * to new breakpoint system (props.tablet and props.mobile)
 *
 * This migration:
 * 1. Finds all Form nodes in the JSON
 * 2. Checks if they have detachablePropsState in custom
 * 3. Moves tablet and mobile values from detachablePropsState to props.tablet and props.mobile
 * 4. Deletes detachablePropsState from custom (no longer needed)
 */
export default function toV8<T extends MigratableJson>(data: T): T {
  const allNodes = data.getAllNodes();

  for (const { node } of allNodes) {
    // Only migrate Form nodes
    if (node.displayName !== FORM_NODE_NAME) {
      continue;
    }

    const custom = node.custom || {};
    const detachablePropsState = custom.detachablePropsState as
      | NodeDetachablePropsState
      | undefined;

    // Skip if no detachablePropsState exists
    if (!detachablePropsState) {
      continue;
    }

    // Get existing props, ensuring it's a plain object
    const props = { ...(node.props || {}) };

    // Migrate tablet breakpoint props
    if (
      detachablePropsState.tablet &&
      typeof detachablePropsState.tablet === 'object' &&
      Object.keys(detachablePropsState.tablet).length > 0
    ) {
      // Initialize tablet props as it does not exist in the node.props
      props.tablet = {};
      // Merge tablet props (don't overwrite if already exists)
      Object.assign(props.tablet as object, detachablePropsState.tablet);
    }

    // Migrate mobile breakpoint props
    if (
      detachablePropsState.mobile &&
      typeof detachablePropsState.mobile === 'object' &&
      Object.keys(detachablePropsState.mobile).length > 0
    ) {
      // Initialize mobile props as it does not exist in the node.props
      props.mobile = {};
      // Merge mobile props (don't overwrite if already exists)
      Object.assign(props.mobile as object, detachablePropsState.mobile);
    }

    // Delete detachablePropsState as it's no longer needed after migration
    delete custom.detachablePropsState;

    // Update node with migrated data
    node.props = props;
    node.custom = custom;
  }

  return data;
}
